import { Col, Layout, Row } from "antd";
import { Content, Footer, Header } from "antd/es/layout/layout";
import { useEffect } from "react";
import { Area } from "./Area";
import { DataViewProps } from "./DataView";
import { Identifiable } from "./Identifiable";
import { ListProps } from "./List";
import { PagingProps } from "./Paging";
import { Section } from "./Section";


export function useFilteredList<TFilter, T extends Identifiable>(props: {
    filterProps: DataViewProps<TFilter>,
    listProps: ListProps<T>,
    applyFilter: (filter: TFilter | undefined) => Promise<T[] | undefined>,
}) {

    const { data: filter } = props.filterProps;
    const { setData: setList } = props.listProps;

    useEffect(() => {
        props.applyFilter(filter).then((result) => {
            setList(result? result : []);
        });
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    return props;

}

export function useApiFilteredList<TFilter, T extends Identifiable>(props: {
    filterProps: DataViewProps<TFilter>,
    listProps: ListProps<T>,
    filterToQueryParams?: (filter: TFilter) => any,
    apiQueryFunc: (queryParams: any) => Promise<{ data: T[] }> 
}) {

    const filterToQueryParams = props.filterToQueryParams || ((filter: TFilter) => { return filter; });
    
    const _props = props as any;
    _props.applyFilter = (filter: TFilter | undefined) => {
        if (filter === undefined) return Promise.resolve(undefined);
        return (
            props
                .apiQueryFunc(filterToQueryParams(filter))
                .then((response) => {
                    return response && response.data;
                })
        );
    }

    return useFilteredList<TFilter, T>(_props);
}



export function useFilteredPaginatedList<TFilter, T extends Identifiable>(props: {
    filterProps: DataViewProps<TFilter>,
    listProps: ListProps<T>,
    pagingProps: PagingProps,
    applyFilter: (filter: TFilter | undefined, page: number , pageSize: number) => Promise<{ data: T[], page: number, total: number } | undefined>,
}) {

    const { data: filter } = props.filterProps;
    const { setData: setList } = props.listProps;

    const pagingProps = props.pagingProps;
    const { page, pageSize } = pagingProps;

    useEffect(() => {
        props.applyFilter(filter, page || 1, pageSize).then((result) => {
            if (result === undefined) {
                setList([]);
                pagingProps.setPage && pagingProps.setPage(1);
                pagingProps.setTotal && pagingProps.setTotal(0);
            } else {
                setList(result.data);
                pagingProps.setPage && pagingProps.setPage(result.page);
                pagingProps.setTotal && pagingProps.setTotal(result.total);
            }
        });
    }, [filter, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    return props;

}

export function useApiFilteredPaginatedList<TFilter, T extends Identifiable>(props: {
    filterProps: DataViewProps<TFilter>,
    listProps: ListProps<T>,
    pagingProps: PagingProps,
    filterToQueryParams?: (filter: TFilter) => any,
    apiQueryFunc: (queryParams: any) => Promise<{ data: { content: T[], totalElements: number } }>
}) {

    const filterToQueryParams = props.filterToQueryParams || ((filter: TFilter | undefined) => { return filter; });
    
    //const _props = props as any;
    const _props = {
        ...props,
        applyFilter: (filter: TFilter | undefined, page: number, pageSize: number) => {
            if (filter === undefined) return Promise.resolve(undefined);
            return (
                props
                    .apiQueryFunc(filterToQueryParams(filter))
                    .then((response) => {
                        let payload = response && response.data;
                        return { data: (payload && payload.content) || [], page: page, total: (payload && payload.totalElements) || 0 };
                    })
            );
        }
    }
    // XXXX
    // _props.applyFilter = (filter: TFilter | undefined, page: number, pageSize: number) => {
    //     if (filter === undefined) return Promise.resolve(undefined);
    //     return (
    //         props
    //             .apiQueryFunc(filterToQueryParams(filter))
    //             .then((response) => {
    //                 let payload = response && response.data;
    //                 return { data: (payload && payload.content) || [], page: page, total: (payload && payload.totalElements) || 0 };
    //             })
    //     );
    // }

    return useFilteredPaginatedList<TFilter, T>(_props);
}

/*
export function useApiTextFilteredPaginatedList<T extends Identifiable>(props: {
    filterProps: DataViewProps<string>,
    listProps: ListProps<T>,
    pagingProps: PagingProps,
    wordToSearchQuery: (w: string) => string, // (w) => `( userName:*${w}* OR email:*${w}* )`
    apiQueryFunc: (queryParams: any) => Promise<{ data: { content: T[], totalElements: number } }>
}) {

    const _props = props as any;

    const textToSearchQuery = (
        text: string,
        wordToSearchQuery: (w: string) => string // (w) => `( userName:*${w}* OR email:*${w}* )`
    ) => {
        let words = (text && text.split(/\s+/).filter(e => e.length).map(wordToSearchQuery)) || [];
        return words.join(' AND ');
    }

    _props.filterToQueryParams = (filter: string) => {
        let search = textToSearchQuery(filter, props.wordToSearchQuery);
        return { search: search };
    }

    return useApiFilteredPaginatedList<string, T>(_props);
}
*/

export function useApiTextFilteredList<T extends Identifiable>(props: {
    filterProps: DataViewProps<string>,
    listProps: ListProps<T>,
    pagingProps: PagingProps,
    apiQueryFunc: (queryParams: any) => Promise<{ data: { content: T[], totalElements: number } }>
}) {
    const _props = props as any;
    _props.filterToQueryParams = (filter: string) => { return { search: filter }; }
    return useApiFilteredPaginatedList<string, T>(_props);
}

export function FilteredPaginatedListLayout(props: {
    filter?: any,
    list: any,
    paginator: any,
}) {

    return (
        <Layout className="h-100">
            {props.filter && 
            <Header>
                <Area>
                    <Section>
                        {props.filter}
                    </Section>
                </Area>
            </Header>
            }
            <Content>
                <Area>
                    <Section>
                        {props.list}
                    </Section>
                </Area>
            </Content>
            <Footer>
                <Row justify={"center"} align={"middle"}>
                    <Col>
                        {props.paginator}
                    </Col>
                </Row>
            </Footer>
        </Layout>
    );

}